import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VehicleImageUpload } from "./VehicleImageUpload";

const vehicleSchema = z.object({
  name: z.string().min(1, "Nickname is required").max(100),
  year: z.coerce.number().min(1900).max(new Date().getFullYear() + 2),
  make: z.string().min(1, "Make is required").max(50),
  model: z.string().min(1, "Model is required").max(50),
  trim: z.string().max(50).optional(),
  trim_variation: z.string().max(50).optional(),
  license_plate: z.string().max(20).optional(),
  vin: z.string().max(17).optional(),
  turo_vehicle_id: z.string().max(50).optional(),
  color: z.string().max(30).optional(),
  mileage: z.coerce.number().min(0).optional(),
});

export type VehicleFormValues = z.infer<typeof vehicleSchema> & {
  image_url?: string | null;
};

interface VehicleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: VehicleFormValues) => void;
  defaultValues?: Partial<VehicleFormValues>;
  isEditing?: boolean;
  isLoading?: boolean;
  /** Pass the vehicle ID when editing so AI generation can work */
  vehicleId?: string;
}

export const VehicleFormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isEditing = false,
  isLoading = false,
  vehicleId,
}: VehicleFormDialogProps) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof vehicleSchema>>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      name: "",
      year: new Date().getFullYear(),
      make: "",
      model: "",
      trim: "",
      trim_variation: "",
      license_plate: "",
      vin: "",
      turo_vehicle_id: "",
      color: "",
      mileage: undefined,
    },
  });

  // Reset form when defaultValues change (for editing)
  useEffect(() => {
    if (open && defaultValues) {
      form.reset({
        name: defaultValues.name ?? "",
        year: defaultValues.year ?? new Date().getFullYear(),
        make: defaultValues.make ?? "",
        model: defaultValues.model ?? "",
        trim: defaultValues.trim ?? "",
        trim_variation: defaultValues.trim_variation ?? "",
        license_plate: defaultValues.license_plate ?? "",
        vin: defaultValues.vin ?? "",
        turo_vehicle_id: defaultValues.turo_vehicle_id ?? "",
        color: defaultValues.color ?? "",
        mileage: defaultValues.mileage ?? undefined,
      });
      setImageUrl(defaultValues.image_url ?? null);
    } else if (open && !isEditing) {
      form.reset();
      setImageUrl(null);
    }
  }, [open, defaultValues, isEditing, form]);

  const handleSubmit = (values: z.infer<typeof vehicleSchema>) => {
    onSubmit({ ...values, image_url: imageUrl });
    if (!isEditing) {
      form.reset();
      setImageUrl(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pr-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>{isEditing ? "Edit Vehicle" : "Add New Vehicle"}</DialogTitle>
              <DialogDescription>
                {isEditing ? "Update your vehicle details below." : "Fill in the details to add a new vehicle."}
              </DialogDescription>
            </div>
            {isEditing && (
              <div className="flex gap-2 shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isLoading} form="vehicle-form">
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>
        <Form {...form}>
          <form id="vehicle-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <VehicleImageUpload
              imageUrl={imageUrl}
              onImageChange={setImageUrl}
              disabled={isLoading}
              vehicleInfo={isEditing && vehicleId ? {
                vehicleId,
                make: form.watch("make"),
                model: form.watch("model"),
                year: form.watch("year"),
                nickname: form.watch("name"),
                color: form.watch("color"),
              } : null}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nickname *</FormLabel>
                  <FormControl>
                    <Input placeholder="My Tesla" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="2024" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Make *</FormLabel>
                    <FormControl>
                      <Input placeholder="Tesla" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model *</FormLabel>
                    <FormControl>
                      <Input placeholder="Model 3" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="trim"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trim</FormLabel>
                    <FormControl>
                      <Input placeholder="Long Range" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="trim_variation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trim Variation</FormLabel>
                  <FormControl>
                    <Input placeholder="Performance Package" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="license_plate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>License Plate</FormLabel>
                    <FormControl>
                      <Input placeholder="ABC-1234" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="vin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>VIN</FormLabel>
                    <FormControl>
                      <Input placeholder="1HGBH41JXMN109186" maxLength={17} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="turo_vehicle_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Turo Vehicle ID</FormLabel>
                  <FormControl>
                    <Input placeholder="123456" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color</FormLabel>
                    <FormControl>
                      <Input placeholder="Midnight Silver" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mileage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mileage</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="15000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : isEditing ? "Save Changes" : "Add Vehicle"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
